/* global Theme */
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import {
  GIFT_REMOVED_KEY,
  giftsToRemoveForVoordeelset,
  handlesWithoutTags,
  isGiftLine,
  noticeExpiry,
  VOORDEELSET_NOTICE_EVENT,
  VOORDEELSET_NOTICE_KEY
} from '@bbd/cart-rules';

/** `/cart.js` has no product tags, they are cached per product handle for the session */
const TAGS_KEY = 'bbd_product_tags';

/**
 * @typedef {{ key: string, handle?: string, properties: Record<string, string> | null }} CartLine
 * @typedef {{ items: CartLine[], item_count: number, sections?: Record<string, string> }} Cart
 */

/**
 * Removes every free gift while a voordeelset is in the cart (PIG-326). A voordeelset already contains a gift, so the
 * gift of the promotion is excluded. This is the one place for that rule, whatever added the gift: the gift picker,
 * the quick-add gift mode or the Monk widget. It lives apart from the gift picker on purpose: the picker only syncs
 * once the Monk funnel is loaded and removes itself when the funnel fails.
 *
 * The customer is told through the notice in the progress bar (assets/bbd-gwp-progress-bar.js), and on the cart page,
 * which has no progress bar, through the toast (assets/bbd-toast.js).
 */
class BbdVoordeelsetGuard extends HTMLElement {
  /** @type {Map<string, string[]>} */
  #tagsByHandle = new Map();
  #checking = false;
  #checkAgain = false;

  connectedCallback() {
    this.#tagsByHandle = this.#readTags();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    // The server knows whether the cart holds a gift: without one there is nothing to check, so no request per page
    if (this.dataset.hasGift === 'true') this.#check();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /** @param {Event} event */
  #onCartUpdate = (event) => {
    const { resource, data } = /** @type {CustomEvent} */ (event).detail ?? {};
    // Our own update: the cart has no gifts left, nothing to check
    if (data?.source === 'bbd-voordeelset-guard') return;

    this.#check(Array.isArray(resource?.items) ? resource : undefined);
  };

  /** @returns {Map<string, string[]>} */
  #readTags() {
    try {
      return new Map(Object.entries(JSON.parse(sessionStorage.getItem(TAGS_KEY) ?? '{}')));
    } catch {
      return new Map();
    }
  }

  #writeTags() {
    try {
      sessionStorage.setItem(TAGS_KEY, JSON.stringify(Object.fromEntries(this.#tagsByHandle)));
    } catch {
      // sessionStorage unavailable, the tags are fetched again next time
    }
  }

  /** @returns {Promise<Cart>} */
  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' });
    return response.json();
  }

  /**
   * Fetches the tags of the products we do not know yet. A failed request leaves the handle unknown, which counts as
   * not a voordeelset: the next cart update tries again.
   * @param {CartLine[]} items
   */
  async #loadTags(items) {
    const root = window.Shopify?.routes?.root ?? '/';
    const unknown = handlesWithoutTags(items, this.#tagsByHandle);
    if (unknown.length === 0) return;

    await Promise.all(
      unknown.map(async (handle) => {
        try {
          const response = await fetch(`${root}products/${handle}.js`);
          if (!response.ok) return;
          const product = await response.json();
          this.#tagsByHandle.set(/** @type {string} */ (handle), Array.isArray(product.tags) ? product.tags : []);
        } catch {
          // Network error: try again on the next cart update
        }
      })
    );
    this.#writeTags();
  }

  /**
   * @param {Cart} [knownCart] - The cart of the update event, saves a request
   */
  async #check(knownCart) {
    if (this.#checking) {
      this.#checkAgain = true;
      return;
    }
    this.#checking = true;

    try {
      const cart = knownCart ?? (await this.#fetchCart());
      // Without a gift there is nothing to remove, so no tags are needed either
      if (!cart.items.some(isGiftLine)) return;

      await this.#loadTags(cart.items);
      const gifts = giftsToRemoveForVoordeelset(cart.items, this.#tagsByHandle);
      if (gifts.length > 0) await this.#removeGifts(gifts);
    } catch (error) {
      console.error('BBD voordeelset guard failed:', error);
    } finally {
      this.#checking = false;
      if (this.#checkAgain) {
        this.#checkAgain = false;
        this.#check();
      }
    }
  }

  /**
   * Removes the gift lines in one request, with the cart sections, and hands the new cart to the theme like the gift
   * picker does. The cart update event is also what the GA4 data layer diffs to report remove_from_cart.
   * @param {CartLine[]} gifts
   */
  async #removeGifts(gifts) {
    const updates = Object.fromEntries(gifts.map((gift) => [gift.key, 0]));
    const ids = [...document.querySelectorAll('cart-items-component')]
      .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : undefined))
      .filter(Boolean);

    const response = await fetch(
      Theme.routes.cart_update_url,
      fetchConfig('json', {
        body: JSON.stringify({ updates, sections: [...new Set(ids)].join(','), sections_url: window.location.pathname })
      })
    );
    if (!response.ok) return;

    /** @type {Cart} */
    const cart = await response.json();
    // The progress line must not say "below the threshold", it is a different reason
    this.#writeStorage(GIFT_REMOVED_KEY, null);
    this.#writeStorage(VOORDEELSET_NOTICE_KEY, String(noticeExpiry(Date.now())));

    document.dispatchEvent(
      new CartUpdateEvent(cart, 'bbd-voordeelset-guard', {
        source: 'bbd-voordeelset-guard',
        itemCount: cart.item_count,
        sections: cart.sections
      })
    );
    document.dispatchEvent(new CustomEvent(VOORDEELSET_NOTICE_EVENT, { bubbles: true }));

    // The cart page has no progress bar, tell the customer with the toast (only rendered there, layout/theme.liquid)
    if (document.querySelector('bbd-toast') && this.dataset.toastMessage) {
      document.dispatchEvent(
        new CustomEvent('bbd:toast', { bubbles: true, detail: { message: this.dataset.toastMessage } })
      );
    }
  }

  /**
   * @param {string} key
   * @param {string | null} value - null removes the key
   */
  #writeStorage(key, value) {
    try {
      if (value === null) sessionStorage.removeItem(key);
      else sessionStorage.setItem(key, value);
    } catch {
      // sessionStorage unavailable, the notice just won't persist
    }
  }
}

if (!customElements.get('bbd-voordeelset-guard')) {
  customElements.define('bbd-voordeelset-guard', BbdVoordeelsetGuard);
}
